import { useEffect, useRef } from 'react';
import { useKey } from 'react-use';

type Callback<T = any> = (data: T) => void;

export function useFrameOuter<T = any>(instanceId: string, callback: Callback<T>) {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    const handleMessage = (event: MessageEvent<T>) => {
      if (event.origin !== window.location.origin) return; // Security check
      callback({ ...event.data, instanceId });
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [callback]); // Removed iframeRef.current from dependencies

  const sendMessage = (message: T) => {
    iframeRef.current?.contentWindow?.postMessage(message, window.location.origin);
  };

  return [sendMessage, iframeRef] as const;
}

export function useFrameInner<T = any>(instanceId: string, callback: Callback<T>) {
  useEffect(() => {
    const handleMessage = (event: MessageEvent<T>) => {
      if (event.origin !== window.location.origin) return; // Security check
      callback({ ...event.data, instanceId });
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [callback]);

  const sendMessage = (message: T) => {
    window.parent.postMessage(message, window.location.origin); // Send message to parent window
  };

  return sendMessage;
}

export function useShortkeys(sendMessage: any) {
  function onR(ev: any) {
    const shiftKey = ev.shiftKey;
    const altKey = ev.altKey;

    sendMessage({
      type: 'ACTION',
      data: { type: 'MODELS', verb: 'randomizeFrame', payload: { altKey, shiftKey } },
    });
  }

  function onToggleEditorMode(mode: string) {
    sendMessage({
      type: 'ACTION',
      data: { type: 'EDITOR', verb: 'toggleMode', payload: { mode } },
    });
  }

  useKey(
    (event) => {
      return event.code === 'KeyR' && event.altKey;
    },
    onR,
    { event: 'keydown' }
  );

  useKey(
    (ev) => ev.code === 'Digit1' && ev.altKey,
    () => onToggleEditorMode('object'),
    {},
    []
  );

  useKey(
    (ev) => ev.code === 'Digit2' && ev.altKey,
    () => onToggleEditorMode('material'),
    {},
    []
  );
}
